import React, {Component} from 'react';
import {toast} from 'react-toastify';
import {withTranslation} from 'react-i18next';

import {APIClient} from "../../../utils/APIClient";
import {API_URL} from '../../../settings/local_settings';
import {LocalStorage} from "../../../utils/LocalStorage";
import {Loading} from "../../Loading";
import {withRouterParams} from "../../../utils/withRouterParams";
import ConnectWithCode from "../../portal/ConnectWithCode";

const MODE_LOADING = 'LOADING';
const MODE_CLIENT = 'CLIENT';

/**
 * Dispatcher de la ruta `/connect-shopify`. Esta ruta es, a la vez, el `redirect_uri` del OAuth de
 * Shopify del flujo agencial (ver `ShopifyLink._getAuthURL`) y el `application_url` de la app de
 * Shopify, así que sobre ella conviven dos flujos que hay que separar:
 *
 *  - Flujo agencial: el usuario de la agencia conecta su propia tienda. Vuelve del OAuth con
 *    `code` + `shop` + `hmac` y estando logueado como AGENCY_USER. Procesamos el callback contra
 *    `get_access_tokens/shopify/` y volvemos a la pantalla de conexiones.
 *  - Onboarding logueado del cliente: llega por el link de la agencia (`?prefill_code=DSH-...`) o
 *    desde el App Store. Delegamos en `ConnectWithCode` (login con Google + redención del código →
 *    Portal). NUNCA tratamos el `code` del OAuth de Shopify como código de conexión DSH.
 */
class ConnectShopify extends Component {
    static apiCallInProgress = false;

    constructor(props) {
        super(props);
        this.state = {mode: MODE_LOADING};
    }

    componentDidMount() {
        this._dispatch();
    }

    _dispatch() {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const shop = params.get('shop');
        const hmac = params.get('hmac');
        const isAgencyUser = LocalStorage.fetchUserType() === 'AGENCY_USER';

        // Flujo agencial: callback de OAuth de Shopify con un usuario de agencia logueado.
        if (code && shop && hmac && isAgencyUser) {
            this._processAgencyCallback({code, shop, hmac, params});
            return;
        }

        // Cualquier otro caso (prefill_code, callback sin ser agencia, install inicial, etc.):
        // onboarding logueado del cliente.
        this.setState({mode: MODE_CLIENT});
    }

    _processAgencyCallback({code, shop, hmac, params}) {
        if (ConnectShopify.apiCallInProgress) {
            return;
        }
        ConnectShopify.apiCallInProgress = true;

        const {t} = this.props;

        // CSRF: validar el `state` contra la expectativa que setea `ShopifyLink` en sessionStorage.
        const state = params.get('state');
        const expectedState = sessionStorage.getItem('shopify_oauth_state');
        sessionStorage.removeItem('shopify_oauth_state');
        if (!expectedState || state !== expectedState) {
            ConnectShopify.apiCallInProgress = false;
            toast.error(t('connect_shopify_agency.security_error'));
            this.props.navigate('/agency/conections');
            return;
        }

        // Recolectar TODOS los query params para que el backend valide el HMAC del install.
        const allParams = {};
        params.forEach((value, key) => {
            allParams[key] = value;
        });

        new APIClient(API_URL + 'get_access_tokens/shopify/')
            .call('POST', {authorization_code: code, shop, hmac, state, all_params: allParams})
            .then((data) => {
                if (data && data.status === 'OK') {
                    toast.success(t('connect_shopify_agency.success'));
                } else {
                    toast.error((data && data.error_message) || t('connect_shopify_agency.error'));
                }
                this.props.navigate('/agency/conections');
            })
            .catch(() => {
                toast.error(t('connect_shopify_agency.error'));
                this.props.navigate('/agency/conections');
            })
            .finally(() => {
                ConnectShopify.apiCallInProgress = false;
            });
    }

    render() {
        if (this.state.mode === MODE_CLIENT) {
            return <ConnectWithCode/>;
        }
        return <Loading message={this.props.t('connect_shopify_agency.connecting')}/>;
    }
}

export default withRouterParams(withTranslation()(ConnectShopify));
